import { useNavigate } from 'react-router-dom';
import Apertavel from '@/components/movimento/Apertavel';
import SeloSituacao from '@/components/feature/SeloSituacao';
import SeloSituacaoInscrito from '@/components/feature/SeloSituacaoInscrito';
import type { SituacaoInscrito } from '@/lib/situacaoInscrito';
import { formatarValor } from '@/pages/evento/utils';
import type { Inscrito } from '../types';

interface Props {
  inscrito: Inscrito;
  saldo: number | null;
  anterior: boolean;
  /** Inscrição presencial que ainda não foi bipada pela direção. */
  aguardandoValidacao?: boolean;
  /** Modalidade que entra pela senha da fila (Campista). */
  usaSenha?: boolean;
  /** O que o inscrito pode ver (sigilo vem da função do banco). */
  situacao?: SituacaoInscrito | null;
  carregandoSituacao?: boolean;
}

export default function CartaoInscricao({
  inscrito,
  saldo,
  anterior,
  aguardandoValidacao = false,
  usaSenha = false,
  situacao = null,
  carregandoSituacao = false,
}: Props) {
  const navigate = useNavigate();

  // Antes da resposta (e quando a inscrição é sigilosa) nada de status real,
  // valor ou "aguardando vaga" — só o selo neutro.
  const neutro = carregandoSituacao || !situacao || situacao.sigilo;
  const suplente = !neutro && situacao?.situacao === 'lista_espera';
  const mostrarValores = !neutro && situacao?.mostrar_valores !== false;

  const linha = [
    inscrito.modalidade,
    inscrito.grupo || null,
    mostrarValores ? formatarValor(inscrito.valor_centavos) : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const mostrarFalta =
    !aguardandoValidacao &&
    !suplente &&
    !neutro &&
    mostrarValores &&
    saldo !== null &&
    saldo > 0 &&
    inscrito.status !== 'cancelada';

  return (
    <Apertavel
      onClick={() => navigate(`/minha-inscricao/${inscrito.codigo}`)}
      className={`w-full cursor-pointer rounded-lg border p-4 text-left transition-colors ${
        anterior
          ? 'border-secondary-200 bg-secondary-50 hover:bg-secondary-100'
          : 'border-secondary-200 bg-white hover:bg-background-100'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <span
            className={`block text-[17px] font-medium ${
              anterior ? 'text-foreground-600' : 'text-foreground-950'
            }`}
          >
            {inscrito.participante}
          </span>
          <span className="mt-1 block text-sm text-secondary-500">{linha}</span>
        </div>
        <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center text-secondary-400">
          <i className="ri-arrow-right-s-line text-xl" />
        </span>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {neutro ? (
          <SeloSituacaoInscrito situacao={situacao} carregando={carregandoSituacao} />
        ) : suplente ? (
          <span className="whitespace-nowrap text-xs font-medium text-secondary-500">
            Você é suplente — avisaremos se abrir uma vaga
          </span>
        ) : usaSenha ? (
          <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-success-50 px-2.5 py-0.5 text-xs font-medium text-success-700">
            <i className="ri-check-line" aria-hidden />
            Inscrito
          </span>
        ) : aguardandoValidacao ? (
          <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-warning-100 px-2.5 py-0.5 text-xs font-medium text-warning-800">
            <i className="ri-time-line" aria-hidden />
            Aguardando validação
          </span>
        ) : (
          <SeloSituacao status={inscrito.status} />
        )}
        {mostrarFalta && (
          <span className="whitespace-nowrap text-sm text-amber-700">
            Falta {formatarValor(saldo as number)}
          </span>
        )}
      </div>
    </Apertavel>
  );
}